// Zwischenspeicher für Lead-Seiten: beim Überfahren einer Listenzeile und für den nächsten Lead vorladen,
// damit die Lead-Seite sofort steht (Ziel < 300 ms). Daten werden beim Öffnen immer frisch nachgeladen.
import { aktivitaetenApi, leadsApi, type Aktivitaet, type LeadDetail, type Liste } from "../api";
import { beimNutzerwechsel } from "./sitzung";

interface Eintrag<T> {
  zeit: number;
  wert: T;
}

const FRISCH_MS = 30_000;
const MAX = 40;
const details = new Map<string, Eintrag<LeadDetail>>();
const feeds = new Map<string, Eintrag<Liste<Aktivitaet>>>();
const laufend = new Map<string, Promise<void>>();

function begrenzen<T>(m: Map<string, T>): void {
  while (m.size > MAX) {
    const erster = m.keys().next().value;
    if (erster === undefined) break;
    m.delete(erster);
  }
}

export function detailAusCache(id: string): LeadDetail | null {
  return details.get(id)?.wert ?? null;
}

export function detailMerken(id: string, d: LeadDetail): void {
  details.delete(id);
  details.set(id, { zeit: Date.now(), wert: d });
  begrenzen(details);
}

export function feedAusCache(id: string): Liste<Aktivitaet> | null {
  return feeds.get(id)?.wert ?? null;
}

export function feedMerken(id: string, f: Liste<Aktivitaet>): void {
  feeds.delete(id);
  feeds.set(id, { zeit: Date.now(), wert: f });
  begrenzen(feeds);
}

export function verwerfen(id: string): void {
  details.delete(id);
  feeds.delete(id);
}

/** Lead und erste Feed-Seite im Hintergrund laden (Fehler werden still ignoriert). */
export function vorladen(id: string): void {
  const d = details.get(id);
  const f = feeds.get(id);
  const jetzt = Date.now();
  if (d && f && jetzt - d.zeit < FRISCH_MS && jetzt - f.zeit < FRISCH_MS) return;
  if (laufend.has(id)) return;
  const p = Promise.allSettled([
    leadsApi.detail(id).then((x) => detailMerken(id, x)),
    aktivitaetenApi.feed(id, { limit: 50 }).then((x) => feedMerken(id, x)),
  ]).then(() => {
    laufend.delete(id);
  });
  laufend.set(id, p);
}

let zeitgeber: number | undefined;
/** Vorladen mit kurzer Verzögerung (Mausbewegung über viele Zeilen löst nicht jedes Mal aus). */
export function vorladenVerzoegert(id: string): void {
  window.clearTimeout(zeitgeber);
  zeitgeber = window.setTimeout(() => vorladen(id), 120);
}

export function vorladenAbbrechen(): void {
  window.clearTimeout(zeitgeber);
}

/** Beim Nutzerwechsel alles Vorgeladene verwerfen. */
export function leadCacheLeeren(): void {
  window.clearTimeout(zeitgeber);
  details.clear();
  feeds.clear();
  laufend.clear();
}

beimNutzerwechsel(leadCacheLeeren);
